import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from 'src/app/shared/models/main-table-filter.model';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { DEPARTMENT_SCOPED_FILTER_PROPERTIES } from '../config/department-scoped-filter-properties.config';

export type TableFilterScope = 'all_deps' | 'department';

export interface UserTableFilterSettings {
  modulePageSizes: { [key: string]: number };
  moduleSearchBy: { [key: string]: MainTableFilterSearchByModel[] };
  moduleSorting: { [key: string]: { orderBy: string; ascDesc: MainTableSortingType } };
  moduleSearchTerm: { [key: string]: string };
}

type ModuleSearchByMap = UserTableFilterSettings['moduleSearchBy'];
type ModuleSortingMap = UserTableFilterSettings['moduleSorting'];
type ModuleSearchTermMap = UserTableFilterSettings['moduleSearchTerm'];

interface StoredTableFilterSettings {
  modulePageSizes: { [key: string]: number };
  moduleSearchBy?: ModuleSearchByMap;
  moduleSorting?: ModuleSortingMap;
  searchByByScope?: Partial<Record<TableFilterScope, ModuleSearchByMap>>;
  sortingByScope?: Partial<Record<TableFilterScope, ModuleSortingMap>>;
  searchTermByScope?: Partial<Record<TableFilterScope, ModuleSearchTermMap>>;
}

const TABLE_FILTERS_STORAGE_KEY_PREFIX = 'tableFilters';
const FILTER_SAVING_ENABLED_KEY_PREFIX = 'filterSavingEnabled';
const DEPARTMENT_SCOPE: TableFilterScope = 'department';
const LEGACY_FILTER_PROPERTY_MAP: Record<string, string> = {
  access: 'accessPositions',
  responsible: 'responsiblePositions',
  responsibleForFollowUp: 'responsibleForFollowUpPositions',
  responsibleForClosing: 'responsibleForClosingPositions',
};

@Injectable({
  providedIn: 'root',
})
export class TableFilterStorageService {
  constructor(private store: Store) {}

  /**
   * All Departments and any single department keep separate table filters, sorting, and search.
   */
  currentTableScope(): TableFilterScope | null {
    const departmentId = this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId);
    if (!departmentId) {
      return null;
    }
    return departmentId === ALL_DEPS_TOKEN ? 'all_deps' : DEPARTMENT_SCOPE;
  }

  /**
   * Checks if filter saving is enabled for the current user.
   * Returns true by default if no preference is stored, and sets it explicitly.
   */
  isFilterSavingEnabled(): boolean {
    const key = this.getFilterSavingEnabledKey();
    if (!key) {
      return true;
    }
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        localStorage.setItem(key, 'true');
        return true;
      }
      return stored === 'true';
    } catch {
      return true;
    }
  }

  /**
   * Checks if there are any saved filters for the current user.
   */
  hasSavedFilters(): boolean {
    const stored = this.readStored();
    if (!stored) {
      return false;
    }
    const hasPageSizes = Object.keys(stored.modulePageSizes || {}).length > 0;
    const hasSearchBy = this.scopeMapsHaveEntries(stored.searchByByScope);
    const hasSorting = this.scopeMapsHaveEntries(stored.sortingByScope);
    const hasSearchTerm = this.scopeMapsHaveEntries(stored.searchTermByScope);
    const hasLegacySearch = Object.keys(stored.moduleSearchBy || {}).length > 0;
    const hasLegacySorting = Object.keys(stored.moduleSorting || {}).length > 0;

    return (
      hasPageSizes ||
      hasSearchBy ||
      hasSorting ||
      hasSearchTerm ||
      hasLegacySearch ||
      hasLegacySorting
    );
  }

  /**
   * Enables or disables filter saving for the current user.
   * When disabled, clears all existing saved filters for this user.
   */
  setFilterSavingEnabled(enabled: boolean): void {
    const key = this.getFilterSavingEnabledKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, String(enabled));
      if (!enabled) {
        this.clearSettings();
      }
    } catch (error) {
      console.error('Failed to save filter saving preference:', error);
    }
  }

  /**
   * Saved filters, sorting, and search for the department context that is selected now.
   */
  loadSettings(): UserTableFilterSettings | null {
    const stored = this.readStored();
    if (!stored) {
      return null;
    }
    return this.projectForScope(stored, this.currentTableScope());
  }

  clearSettings(): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear table filter settings from localStorage:', error);
    }
  }

  saveModulePageSize(module: string, pageSize: number): void {
    if (!this.isFilterSavingEnabled()) return;
    const stored = this.readStored() || this.getDefaultStored();
    stored.modulePageSizes[module] = pageSize;
    this.writeStored(stored);
  }

  saveModuleSearchBy(module: string, searchBy: MainTableFilterSearchByModel[]): void {
    if (!this.isFilterSavingEnabled()) return;
    this.updateCurrentScope(module, { searchBy });
  }

  saveModuleSorting(
    module: string,
    sorting: { orderBy: string; ascDesc: MainTableSortingType }
  ): void {
    if (!this.isFilterSavingEnabled()) return;
    this.updateCurrentScope(module, { sorting });
  }

  saveModuleSearchTerm(module: string, searchTerm: string): void {
    if (!this.isFilterSavingEnabled()) return;
    this.updateCurrentScope(module, { searchTerm });
  }

  /**
   * Clears saved filters, sorting, and search for one module in the current department context.
   */
  clearModuleFilters(module: string): void {
    const scope = this.currentTableScope();
    const stored = this.readStored();
    if (!stored || !scope) {
      return;
    }
    const searchBy = { ...(stored.searchByByScope?.[scope] ?? {}) };
    const sorting = { ...(stored.sortingByScope?.[scope] ?? {}) };
    const searchTerms = { ...(stored.searchTermByScope?.[scope] ?? {}) };
    delete searchBy[module];
    delete sorting[module];
    delete searchTerms[module];
    this.writeScopeSlice(stored, scope, searchBy, sorting, searchTerms);
  }

  clearModuleColumnFilters(module: string, column: string): void {
    const scope = this.currentTableScope();
    const stored = this.readStored();
    if (!stored || !scope) {
      return;
    }

    const searchBy = { ...(stored.searchByByScope?.[scope] ?? {}) };
    const sorting = { ...(stored.sortingByScope?.[scope] ?? {}) };
    const columnFilters = searchBy[module] ?? [];
    const hasColumnInSearch = columnFilters.some((filter) => filter.property === column);
    const hasColumnInSort = sorting[module]?.orderBy === column;
    if (!hasColumnInSearch && !hasColumnInSort) {
      return;
    }

    const remainingSearchBy = columnFilters.filter((filter) => filter.property !== column);
    if (remainingSearchBy.length) {
      searchBy[module] = remainingSearchBy;
    } else {
      delete searchBy[module];
    }
    if (sorting[module]?.orderBy === column) {
      delete sorting[module];
    }

    this.writeScopeSlice(stored, scope, searchBy, sorting, stored.searchTermByScope?.[scope] ?? {});
  }

  /**
   * Clears saved filters, sorting, and search for both department contexts.
   * Called when the user switches tenant. Page size is kept.
   */
  clearAllFiltersOnTenantChange(): void {
    const stored = this.readStored();
    if (!stored) {
      return;
    }
    stored.moduleSearchBy = {};
    stored.moduleSorting = {};
    stored.searchByByScope = {};
    stored.sortingByScope = {};
    stored.searchTermByScope = {};
    this.writeStored(stored);
  }

  /**
   * Removes filters that point at a specific department's positions or components.
   * Only the single-department context is changed. All Departments keeps its own filters.
   */
  clearDepartmentScopedFiltersOnDepartmentChange(): void {
    const stored = this.readStored();
    const departmentSearch = stored?.searchByByScope?.[DEPARTMENT_SCOPE];
    if (!stored || !departmentSearch) {
      return;
    }
    stored.searchByByScope = {
      ...stored.searchByByScope,
      [DEPARTMENT_SCOPE]: this.withoutDepartmentScopedFilters(departmentSearch),
    };
    this.writeStored(stored);
  }

  private updateCurrentScope(
    module: string,
    change: {
      searchBy?: MainTableFilterSearchByModel[];
      sorting?: { orderBy: string; ascDesc: MainTableSortingType };
      searchTerm?: string;
    }
  ): void {
    const scope = this.currentTableScope();
    if (!scope) {
      return;
    }
    const stored = this.readStored() || this.getDefaultStored();
    const searchBy = { ...(stored.searchByByScope?.[scope] ?? {}) };
    const sorting = { ...(stored.sortingByScope?.[scope] ?? {}) };
    const searchTerms = { ...(stored.searchTermByScope?.[scope] ?? {}) };
    if (change.searchBy) {
      searchBy[module] = change.searchBy;
    }
    if (change.sorting) {
      sorting[module] = change.sorting;
    }
    if (change.searchTerm !== undefined) {
      searchTerms[module] = change.searchTerm;
    }
    this.writeScopeSlice(stored, scope, searchBy, sorting, searchTerms);
  }

  private writeScopeSlice(
    stored: StoredTableFilterSettings,
    scope: TableFilterScope,
    searchBy: ModuleSearchByMap,
    sorting: ModuleSortingMap,
    searchTerms: ModuleSearchTermMap
  ): void {
    stored.searchByByScope = { ...(stored.searchByByScope ?? {}), [scope]: searchBy };
    stored.sortingByScope = { ...(stored.sortingByScope ?? {}), [scope]: sorting };
    stored.searchTermByScope = { ...(stored.searchTermByScope ?? {}), [scope]: searchTerms };
    this.writeStored(stored);
  }

  private withoutDepartmentScopedFilters(searchBy: ModuleSearchByMap): ModuleSearchByMap {
    const next: ModuleSearchByMap = {};
    Object.entries(searchBy).forEach(([module, filters]) => {
      next[module] = (filters || []).filter(
        (filter) => !DEPARTMENT_SCOPED_FILTER_PROPERTIES.includes(filter.property)
      );
    });
    return next;
  }

  private getFilterSavingEnabledKey(): string | null {
    const userId = this.currentUserId();
    if (!userId) {
      return null;
    }
    return `${FILTER_SAVING_ENABLED_KEY_PREFIX}_${userId}`;
  }

  private getStorageKey(): string | null {
    const userId = this.currentUserId();
    if (!userId) {
      console.warn(
        '[TableFilterStorageService] Could not determine user ID when creating storage key for table filters.'
      );
      return null;
    }
    return `${TABLE_FILTERS_STORAGE_KEY_PREFIX}_${userId}`;
  }

  private currentUserId(): string | null {
    return this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
  }

  private readStored(): StoredTableFilterSettings | null {
    const key = this.getStorageKey();
    if (!key) {
      return null;
    }
    try {
      const storedText = localStorage.getItem(key);
      if (!storedText) {
        return null;
      }
      const stored = JSON.parse(storedText) as StoredTableFilterSettings;
      const migrated = this.migrateStored(stored);
      if (migrated.changed) {
        this.writeStored(migrated.stored);
      }
      return migrated.stored;
    } catch (error) {
      console.error('Failed to load table filter settings from localStorage:', error);
      return null;
    }
  }

  private writeStored(stored: StoredTableFilterSettings): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(stored));
    } catch (error) {
      console.error('Failed to save table filter settings to localStorage:', error);
    }
  }

  private projectForScope(
    stored: StoredTableFilterSettings,
    scope: TableFilterScope | null
  ): UserTableFilterSettings {
    if (!scope || !stored.searchByByScope) {
      return {
        modulePageSizes: stored.modulePageSizes || {},
        moduleSearchBy: stored.moduleSearchBy || {},
        moduleSorting: stored.moduleSorting || {},
        moduleSearchTerm: {},
      };
    }
    return {
      modulePageSizes: stored.modulePageSizes || {},
      moduleSearchBy: stored.searchByByScope[scope] || {},
      moduleSorting: stored.sortingByScope?.[scope] || {},
      moduleSearchTerm: stored.searchTermByScope?.[scope] || {},
    };
  }

  private migrateStored(stored: StoredTableFilterSettings): {
    changed: boolean;
    stored: StoredTableFilterSettings;
  } {
    const next: StoredTableFilterSettings = {
      ...stored,
      modulePageSizes: stored.modulePageSizes || {},
    };
    let changed = false;
    const legacySearch = this.migrateSearchByMap(next.moduleSearchBy);
    if (legacySearch.changed) {
      next.moduleSearchBy = legacySearch.searchBy;
      changed = true;
    }
    const migratedScopes = this.migrateScopedSearch(next.searchByByScope);
    if (migratedScopes.changed) {
      next.searchByByScope = migratedScopes.maps;
      changed = true;
    }
    if (this.ensureScopeBuckets(next)) {
      changed = true;
    }
    return { changed, stored: next };
  }

  private ensureScopeBuckets(stored: StoredTableFilterSettings): boolean {
    const scope = this.currentTableScope();
    if (!scope || stored.searchByByScope) {
      return false;
    }
    stored.searchByByScope = { [scope]: stored.moduleSearchBy ?? {} };
    stored.sortingByScope = { [scope]: stored.moduleSorting ?? {} };
    stored.searchTermByScope = stored.searchTermByScope ?? {};
    stored.moduleSearchBy = {};
    stored.moduleSorting = {};
    return true;
  }

  private migrateScopedSearch(maps: StoredTableFilterSettings['searchByByScope']): {
    changed: boolean;
    maps: StoredTableFilterSettings['searchByByScope'];
  } {
    if (!maps) {
      return { changed: false, maps };
    }
    let changed = false;
    const nextMaps = { ...maps };
    (Object.keys(nextMaps) as TableFilterScope[]).forEach((scope) => {
      const migrated = this.migrateSearchByMap(nextMaps[scope]);
      if (migrated.changed) {
        nextMaps[scope] = migrated.searchBy;
        changed = true;
      }
    });
    return { changed, maps: nextMaps };
  }

  private migrateSearchByMap(filtersByModule: ModuleSearchByMap | undefined): {
    changed: boolean;
    searchBy: ModuleSearchByMap;
  } {
    let changed = false;
    const searchBy: ModuleSearchByMap = {};
    Object.entries(filtersByModule || {}).forEach(([module, filters]) => {
      searchBy[module] = this.migrateModuleFilters(filters, () => {
        changed = true;
      });
    });
    return { changed, searchBy };
  }

  private migrateModuleFilters(
    filters: MainTableFilterSearchByModel[] | undefined,
    markChanged: () => void
  ): MainTableFilterSearchByModel[] {
    return (filters || []).map((filter) => {
      const migrated = this.migrateFilterProperty(filter);
      if (migrated !== filter) {
        markChanged();
      }
      return migrated;
    });
  }

  private migrateFilterProperty(
    filter: MainTableFilterSearchByModel
  ): MainTableFilterSearchByModel {
    const migratedProperty = LEGACY_FILTER_PROPERTY_MAP[filter.property];
    if (!migratedProperty) {
      return filter;
    }
    return new MainTableFilterSearchByModel(migratedProperty, filter.value, filter.operand);
  }

  private scopeMapsHaveEntries(
    maps: Partial<Record<TableFilterScope, { [module: string]: unknown }>> | undefined
  ): boolean {
    if (!maps) {
      return false;
    }
    return Object.values(maps).some((moduleMap) => Object.keys(moduleMap || {}).length > 0);
  }

  private getDefaultStored(): StoredTableFilterSettings {
    return {
      modulePageSizes: {},
      moduleSearchBy: {},
      moduleSorting: {},
      searchByByScope: {},
      sortingByScope: {},
      searchTermByScope: {},
    };
  }
}
